import React from 'react'
import {useTranslation} from 'react-i18next'
import {useEnumTranslations} from '@common/utils/translations'
import {EnumType} from '@common/utils/types'
import type {PartnerPatientAppointmentResponse} from '@generated/models'
import {SlotStatusType} from '@common/models'

import {
  ListCard,
  Row,
  RowLeft,
  ActionsContainer,
  HistoryButton,
  ActionButton,
  EmptyState,
} from './ReportsPatientPage.styled'

import {Typography} from '~/components/general/typography/Typography'
import {Container} from '~/components/general/container/Container'
import {Icon} from '~/components/general/icon/Icon'

type PatientAppointmentsListProps = {
  title: string
  items: PartnerPatientAppointmentResponse[]
  onClickHistory: (slotId: string) => void
  onClickCancel?: (slotId: string) => void
  onClickReschedule?: (slotId: string) => void
}

export const PatientAppointmentsList = ({
  title,
  items,
  onClickHistory,
  onClickCancel,
  onClickReschedule,
}: PatientAppointmentsListProps) => {
  const {t} = useTranslation()
  const {translateEnumValue} = useEnumTranslations()

  return (
    <ListCard>
      <Typography weight="bold" size="lg">
        {title}
      </Typography>
      <Container direction="column" spacing={0}>
        {items.map((item) => (
          <Row key={item.slotId}>
            <RowLeft $cancelled={item.status === 'CANCELLED'}>
              <Typography weight="bold">{`${item.date} ${item.time}`}</Typography>
              <Typography>
                {item.serviceName && item.practitionerName
                  ? `${item.serviceName} - ${item.practitionerName}`
                  : item.serviceName || item.practitionerName || '-'}
              </Typography>
              <Typography>
                {translateEnumValue(
                  EnumType.SlotStatusType,
                  item.status as SlotStatusType,
                  '-',
                )}
              </Typography>
            </RowLeft>
            <ActionsContainer>
              {onClickReschedule &&
                (item.status === 'SCHEDULED' ||
                  item.status === 'FULFILLED' ||
                  item.status === 'NO_SHOW') && (
                  <ActionButton
                    color="secondary"
                    size="sm"
                    onClick={() => onClickReschedule(item.slotId)}
                    startIcon={<Icon name="Edit" size="sm" />}>
                    {t('Reschedule')}
                  </ActionButton>
                )}
              {onClickCancel &&
                (item.status === 'SCHEDULED' ||
                  item.status === 'FULFILLED' ||
                  item.status === 'NO_SHOW') && (
                  <ActionButton
                    color="secondary"
                    size="sm"
                    onClick={() => onClickCancel(item.slotId)}
                    startIcon={<Icon name="Close" size="sm" />}>
                    {t('Cancel')}
                  </ActionButton>
                )}
              <HistoryButton
                color="primary"
                size="sm"
                onClick={() => onClickHistory(item.slotId)}
                startIcon={<Icon name="DocumentList" size="sm" />}>
                {t('History')}
              </HistoryButton>
            </ActionsContainer>
          </Row>
        ))}
        {items.length === 0 && (
          <EmptyState>
            <Typography color="muted">—</Typography>
          </EmptyState>
        )}
      </Container>
    </ListCard>
  )
}
